import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, ViewChild, ElementRef, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import { ScrollToTop } from '../../components/scroll-to-top/scroll-to-top';
import { TicketSelector } from '../../components/ticket-selector/ticket-selector';
import { Event, EventsService, Subevento } from '../../core/services/events.service';
import { TipoTicket, TicketsService } from '../../core/services/tickets.service';
import { MetaService } from '../../core/services/meta.service';
import { WebAppConfigService } from '../../core/services/webapp-config.service';
import { API_CONFIG } from '../../core/config/api.config';
import googleMapsApiKey from '../../core/config/google-maps.config';


declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

@Component({
  selector: 'app-evento-template',
  imports: [CommonModule, FormsModule, Header, Footer, ScrollToTop, TicketSelector],
  templateUrl: './evento-template.html',
  styleUrl: './evento-template.css',
})
export class EventoTemplate implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(Header) header?: Header;
  @ViewChild('mapContainer', { static: false }) mapContainer?: ElementRef;

  event?: Event;
  isLoading = true;
  error = false;
  subeventos: Subevento[] = [];
  isLoadingSubeventos = false;
  errorSubeventos = false;
  defaultTipoTickets: TipoTicket[] = [];
  isLoadingDefaultTipoTickets = false;
  errorDefaultTipoTickets = false;
  hasSeatmapInDefaultEvent = false;
  selectedDefaultTipoTicketId = '';
  private map: any;
  private platformId = inject(PLATFORM_ID);
  private isBrowser: boolean;
  private googleMapsLoaded = false;
  private googleMapsApiKey: string | null = null;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsService: EventsService,
    private ticketsService: TicketsService,
    private metaService: MetaService,
    private webAppConfig: WebAppConfigService,    
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const urlSlug = params['url_slug'];
      if (urlSlug) {
        this.loadEvent(urlSlug);
      }
    });
  }

  ngOnDestroy(): void {


    // Resetear meta tags al salir de la página del evento
    const config = this.webAppConfig.config();
    if (config) {
      this.metaService.resetToDefault(
        this.webAppConfig.getSiteName() + ' - ' + config.short_description,
        config.description,
        config.url_logo,
        config.url
      );
    }
  }

  loadEvent(urlSlug: string): void {
    this.isLoading = true;
    this.error = false;

    console.log('Loading event with slug:', urlSlug);

    this.eventsService.getEventBySlug(urlSlug).subscribe({
      next: (response) => {
        console.log('Event response:', response);
        if (response.success && response.data) {
          this.event = response.data;
          console.log('Event loaded:', this.event);
          console.log('🎟️ [DEBUG] tipoevento:', this.event.tipoevento, '| max_tickets_por_compra:', this.event.max_tickets_por_compra);

          // Actualizar meta tags con información del evento
          const baseUrl = this.webAppConfig.config()?.url;
          this.metaService.updateEventMeta(this.event, baseUrl);

          this.isLoading = false;
          this.cdr.markForCheck();

          // Cargar subeventos si es evento tipo tren
          if (this.isTrenEvent()) {
            this.loadSubeventos(this.event.eventoid);
          } else {
            this.loadDefaultTipoTickets(this.event.eventoid);
          }

          // Cargar el mapa si tenemos localización
          if (this.isBrowser && this.event.localizacion) {
            setTimeout(() => this.loadGoogleMaps(), 100);
          }
        } else {
          console.error('Invalid response structure');
          this.error = true;
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Error loading event:', error);
        this.error = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  getEventImage(type: 'cuadrada' | 'horizontal' | 'banner'): string {
    if (!this.event) return this.eventsService.getDefaultImage(type);
    return this.eventsService.getEventImage(this.event, type);
  }

  formatDate(dateString: string): string {
    if (!this.event) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: this.event.timezone || 'America/Guayaquil'
    });
  }

  formatTime(dateString: string): string {
    if (!this.event) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: this.event.timezone || 'America/Guayaquil'
    });
  }

  isTrenEvent(): boolean {
    return this.event?.tipoevento === 'tren';
  }

  loadSubeventos(eventoid: string): void {
    this.isLoadingSubeventos = true;
    this.errorSubeventos = false;
    this.eventsService.getSubeventos(eventoid).subscribe({
      next: (response) => {
        if (response.success && Array.isArray(response.data)) {
          this.subeventos = response.data;
        } else {
          this.errorSubeventos = true;
          console.error('Error cargando subeventos:', (response as any).message);
        }
        this.isLoadingSubeventos = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error HTTP cargando subeventos:', err);
        this.errorSubeventos = true;
        this.isLoadingSubeventos = false;
        this.cdr.markForCheck();
      }
    });
  }

  navigateToActividad(subevento: Subevento): void {
    this.router.navigate(['/evento', this.event!.url_slug, 'actividad', subevento.slug]);
  }

  getSubeventoImage(subevento: Subevento, type: 'cuadrada' | 'banner'): string {
    return subevento.imagenes[type] || this.getEventImage(type);
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  onTicketsAdded(): void {
    // Refrescar el contador del carrito en el header
    if (this.header) {
      this.header.loadCartCount();
    }
  }

  private loadDefaultTipoTickets(eventoid: string): void {
    this.isLoadingDefaultTipoTickets = true;
    this.errorDefaultTipoTickets = false;
    this.defaultTipoTickets = [];
    this.hasSeatmapInDefaultEvent = false;
    this.selectedDefaultTipoTicketId = '';

    this.ticketsService.getTiposTicketByEvento(eventoid).subscribe({
      next: (response) => {
        if (response.success) {
          const canalWeb = this.event?.canales?.find((c) => c.canal === 'web');
          const permitirCanalWeb = canalWeb?.publicado === true && canalWeb?.disponible === true;

          const tiposFiltrados = (response.data || [])
            .filter((tipo) => (tipo.estado || '').toLowerCase() === 'activo')
            .map((tipo) => ({
              ...tipo,
              precios: (tipo.precios || []).filter((precio) => {
                if (permitirCanalWeb && precio.canal === 'web') {
                  return precio.publicado === true && precio.disponible === true;
                }
                return false;
              })
            }))
            .filter((tipo) => (tipo.precios || []).length > 0);

          this.defaultTipoTickets = tiposFiltrados;
          this.hasSeatmapInDefaultEvent = tiposFiltrados.some((tt) => !!tt.seatmapid);

          if (this.hasSeatmapInDefaultEvent && this.defaultTipoTickets.length > 0) {
            this.selectedDefaultTipoTicketId = this.defaultTipoTickets[0].tipoticketid;
          }
        }

        this.isLoadingDefaultTipoTickets = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorDefaultTipoTickets = true;
        this.isLoadingDefaultTipoTickets = false;
        this.cdr.markForCheck();
      }
    });
  }

  getDefaultTipoTicketPrecioDesde(tipoTicket: TipoTicket): number | null {
    if (!tipoTicket.precios || tipoTicket.precios.length === 0) {
      return null;
    }
    return Math.min(...tipoTicket.precios.map((p) => p.precio_total ?? p.precio_base ?? 0));
  }

  getDefaultTipoTicketMoneda(tipoTicket: TipoTicket): string {
    return tipoTicket.precios?.[0]?.nombre_moneda || 'USD';
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  goToComprarLocalidad(): void {
    if (!this.event || !this.selectedDefaultTipoTicketId) {
      return;
    }

    this.router.navigate([
      '/evento',
      this.event.url_slug,
      'seatmap',
      'comprar',
      this.selectedDefaultTipoTicketId
    ]);
  }

  

  ngAfterViewInit(): void {
    // El mapa se inicializa desde loadEvent() después de que el evento se carga
    // y el template se renderiza. No llamar aquí porque event puede no existir aún.
  }

  private loadGoogleMaps(): void {
    if (!this.isBrowser) return;

    // Si ya tenemos el API key y Google Maps está cargado
    if (this.googleMapsApiKey && window.google && window.google.maps) {
      this.waitForMapContainer();
      return;
    }

    // Si ya tenemos el API key pero Google Maps no está cargado
    if (this.googleMapsApiKey) {
      this.loadGoogleMapsScript();
      return;
    }

    // Obtener el API key desde la configuración
    if (googleMapsApiKey.success && googleMapsApiKey.data?.apiKey) {
      this.googleMapsApiKey = googleMapsApiKey.data.apiKey;
      console.log('Google Maps API key cargado desde configuración');
      this.loadGoogleMapsScript();
    } else {
      console.error('No se pudo obtener el API key de Google Maps desde la configuración');
    }
  }

  /**
   * Espera a que el mapContainer esté disponible en el DOM (puede tardar
   * porque está dentro de un @if condicional). Reintenta hasta 10 veces.
   */
  private waitForMapContainer(retries = 10): void {
    if (this.mapContainer) {
      this.initializeMap();
      return;
    }
    if (retries > 0) {
      this.cdr.detectChanges();
      setTimeout(() => this.waitForMapContainer(retries - 1), 150);
    } else {
      console.warn('mapContainer no disponible después de varios intentos');
    }
  }

  private loadGoogleMapsScript(): void {
    if (!this.isBrowser || !this.googleMapsApiKey) return;

    // Verificar si Google Maps ya está cargado
    if (window.google && window.google.maps) {
      this.waitForMapContainer();
      return;
    }

    // Verificar si el script ya existe en el DOM
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`);
    if (existingScript) {
      if (window.google && window.google.maps) {
        this.waitForMapContainer();
      } else {
        existingScript.addEventListener('load', () => {
          this.googleMapsLoaded = true;
          this.waitForMapContainer();
        });
      }
      return;
    }

    // Cargar el script de Google Maps
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.googleMapsLoaded = true;
      this.waitForMapContainer();
    };
    script.onerror = (error) => {
      console.error('Error loading Google Maps:', error);
    };
    document.head.appendChild(script);
  }

  private initializeMap(): void {
    if (!this.isBrowser || !this.event?.localizacion || !this.mapContainer) {
      return;
    }

    const { lat, lng } = this.event.localizacion;

    // Verificar que las coordenadas sean válidas
    if (!lat || !lng) {
      console.warn('Coordenadas no válidas para el mapa');
      return;
    }

    try {
      const mapOptions = {
        center: { lat, lng },
        zoom: 15,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
      };

      this.map = new window.google.maps.Map(
        this.mapContainer.nativeElement,
        mapOptions
      );

      // Agregar marcador en la ubicación del evento
      new window.google.maps.Marker({
        position: { lat, lng },
        map: this.map,
        title: this.event.nombre,
        animation: window.google.maps.Animation.DROP,
      });

      console.log('Mapa inicializado correctamente');
    } catch (error) {
      console.error('Error al inicializar el mapa:', error);
    }
  }
}

